import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface AuthCardProps {
  children: ReactNode;
  className?: string;
  maxWidth?: string;
}

const AuthCard = ({
  children,
  className,
  maxWidth = "max-w-[480px]",
}: AuthCardProps) => {
  return (
    <div className={cn("w-full", maxWidth, "animate-fadeInUp", className)}>
      {children}
    </div>
  );
};

interface AuthCardContainerProps {
  children: ReactNode;
  className?: string;
}

const AuthCardContainer = ({ children, className }: AuthCardContainerProps) => {
  return (
    <div
      className={cn(
        "bg-white/5 backdrop-blur-[20px] border border-white/10 rounded-[20px] p-8 mb-6 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]",
        className
      )}
    >
      {children}
    </div>
  );
};

interface AuthCardHeaderProps {
  children: ReactNode;
  className?: string;
}

const AuthCardHeader = ({ children, className }: AuthCardHeaderProps) => {
  return <div className={cn("text-center mb-8", className)}>{children}</div>;
};

interface AuthCardLogoProps {
  src: string;
  alt: string;
  className?: string;
}

const AuthCardLogo = ({ src, alt, className }: AuthCardLogoProps) => {
  return (
    <div className="relative inline-block mb-6">
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gradient-to-r from-[rgba(120,119,198,0.4)] to-transparent rounded-full blur-lg animate-pulse"></div>
      <img className={cn("mb-2 h-16 w-auto", className)} src={src} alt={alt} />
    </div>
  );
};

interface AuthCardTitleProps {
  children: ReactNode;
  className?: string;
}

const AuthCardTitle = ({ children, className }: AuthCardTitleProps) => {
  return (
    <div className={cn("text-lg text-white/80 font-medium", className)}>
      {children}
    </div>
  );
};

interface AuthCardDividerProps {
  text: string;
  className?: string;
}

const AuthCardDivider = ({ text, className }: AuthCardDividerProps) => {
  return (
    <div className={cn("text-center mt-6 relative", className)}>
      <div className="absolute top-1/2 left-0 right-0 h-px bg-white/20"></div>
      <span className="bg-[rgba(15,15,35,0.9)] px-4 text-white/60 text-sm">
        {text}
      </span>
    </div>
  );
};

interface AuthCardFeatureProps {
  icon: ReactNode;
  children: ReactNode;
  className?: string;
}

const AuthCardFeature = ({
  icon,
  children,
  className,
}: AuthCardFeatureProps) => {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white/80 text-sm font-medium transition-all duration-200 hover:bg-white/8 hover:border-[rgba(120,119,198,0.3)]",
        className
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
};

interface AuthCardFeaturesProps {
  children: ReactNode;
  className?: string;
}

const AuthCardFeatures = ({ children, className }: AuthCardFeaturesProps) => {
  return (
    <div className={cn("flex gap-4 justify-center flex-wrap", className)}>
      {children}
    </div>
  );
};

export {
  AuthCard,
  AuthCardContainer,
  AuthCardHeader,
  AuthCardLogo,
  AuthCardTitle,
  AuthCardDivider,
  AuthCardFeature,
  AuthCardFeatures,
};
